import Vue from 'vue';
import axios from 'axios';
import {addAuthKey} from '@/helpers/addAuthKeyToUrl';

const apiUrl = `${process.env.VUE_APP_BASE_URL || '/'}index.php`;

function appendParamsInFormData(params) {
    const formData = new FormData();

    if (params.page === 'information') {
        formData.append('name', params.name);

        if (params.filials && params.filials.length) {
            params.filials.forEach(filial => {
                formData.append('filials[]', filial.value);
            });
        } else {
            formData.append('filials[]', '');
        }

        if (params.typePosts && params.typePosts.length) {
            params.typePosts.forEach(typePost => {
                formData.append('executives[typePosts][]', typePost.value);
            });
        } else {
            formData.append('executives[typePosts][]', '');
        }

        if (params.posts && params.posts.length) {
            params.posts.forEach((post, i) => {
                if (post.code && post.code.value) {
                    formData.append(`executives[posts][${i}][code]`,
                        post.code.value);

                    if (post.specialities.length) {
                        post.specialities.forEach((speciality, j) => {
                            formData.append(
                                `executives[posts][${i}][specialities][]`,
                                speciality.value);
                        });
                    }

                    if (post.categories.length) {
                        post.categories.forEach((category, k) => {
                            formData.append(`executives[posts][${i}][ranks][]`,
                                category.value);
                        });
                    }
                }
            });
        } else {
            formData.append('executives[posts]', '');
        }

        if (params.workers && params.workers.length) {
            params.workers.forEach(worker => {
                formData.append('executives[workers][]', worker.id);
            });
        } else {
            formData.append('executives[workers][]', '');
        }

        if (params.responsible && params.responsible.length) {
            params.responsible.forEach(responsibleItem => {
                formData.append('responsible[]', responsibleItem.value);
            });
        } else {
            formData.append('responsible[]', '');
        }

        formData.append('labor_costs', params.laborCosts);

        if (params.repeatInterval) {
            formData.append('repeat_interval', params.repeatInterval);
            formData.append('repeat_type', params.repeatType.value);
        } else {
            formData.append('repeat_interval', '');
            formData.append('repeat_type', '');
        }

        if (params.tools && params.tools.length) {
            params.tools.forEach(tool => {
                formData.append('tools[]', tool.value);
            });
        } else {
            formData.append('tools[]', '');
        }

        formData.append('tasks', params.tasks);

        formData.append('vep', params.vep);

        formData.append('comment', params.comment);

        if (params.links && params.links.length) {
            params.links.forEach(link => {
                formData.append('links[]', link.value);
            });
        } else {
            formData.append('links[]', '');
        }

        if (params.files && params.files.length) {
            params.files.forEach(file => {
                formData.append('files[]', file);
            });
        } else {
            formData.append('files[]', '');
        }

        if (params.oldFiles && params.oldFiles.length) {
            params.oldFiles.forEach(file => {
                formData.append('oldFiles[]', file);
            });
        } else {
            formData.append('oldFiles[]', '');
        }
    }

    if (params.page === 'indicators') {
        if (params.indicators && params.indicators.length) {
            params.indicators.forEach(indicator => {
                formData.append('indicators[]', indicator.value.value);
            });
        } else {
            formData.append('indicators[]', '');
        }
    }

    if (params.page === 'debugging') {
        formData.append('status_id', params.statusId);

        if (params.checkoutTasks && params.checkoutTasks.length) {
            params.checkoutTasks.forEach(checkoutTask => {
                formData.append('checkoutTasks[]', checkoutTask.value);
            });
        } else {
            formData.append('checkoutTasks[]', '');
        }
    }

    return formData;
}

const state = () => ({
    workersTotalCount: 0,
    workersPageCount: 0,
    workers: [],
    defaultPerformers: [],
    checkboxAllWorkers: true,
    functionModel: {},
    userTableColumns: [],
    tableColumns: [],
    totalItemsCount: 0,
    totalPages: 1,
});

const mutations = {
    setWorkers(state, {workers, page}) {
        if (!page || page === 1) {
            state.workers = workers;
        } else {
            state.workers = [...state.workers, ...workers];
        }
    },
    setWorkersTotalCount(state, workersTotalCount) {
        state.workersTotalCount = workersTotalCount;
    },
    setWorkersPageCount(state, workersPageCount) {
        state.workersPageCount = workersPageCount;
    },

    setFunctionModel(state, functionModel) {
        state.functionModel = functionModel;

        if (functionModel.executives && functionModel.executives.workers &&
            functionModel.executives.workers.length) {
            state.checkboxAllWorkers = false;
            state.defaultPerformers = JSON.parse(
                JSON.stringify(functionModel.executives.workers));
        }
    },
    setDefaultPerformers(state, defaultPerformers) {
        state.defaultPerformers = defaultPerformers;
    },
    setUserTableColumns(state, userTableColumns) {
        state.userTableColumns = userTableColumns;
    },
    setTableColumns(state, tableColumns) {
        state.tableColumns = tableColumns;
    },
    setTotalItemsCount(state, amount) {
        state.totalItemsCount = amount;
    },
    setTotalPages(state, amount) {
        state.totalPages = amount;
    },
    setCheckboxAll(state, checkboxAll) {
        state.checkboxAllWorkers = checkboxAll;
    },
};

const actions = {
    async getWorkersList(ctx, params) {
        const token = ctx.rootState.authToken;

        let queryParams = [];

        if (params.page) queryParams.push(`page=${params.page}`);

        if (params.search) queryParams.push(`name=${params.search}`);

        if (params.filials && params.filials.length) {
            params.filials.forEach(filial => {
                queryParams.push(`filials[]=${filial}`);
            });
        }

        if (params.typePosts && params.typePosts.length) {
            params.typePosts.forEach(typePost => {
                queryParams.push(`typePost[]=${typePost}`);
            });
        }

        if (params.posts && params.posts.length) {
            params.posts.forEach((post, i) => {
                if (post) {
                    queryParams.push(`post[${i}][code]=${post.code}`);

                    if (post.specialities.length) {
                        post.specialities.forEach((speciality, j) => {
                            queryParams.push(
                                `post[${i}][specialities][${j}]=${speciality}`);
                        });
                    }

                    if (post.categories.length) {
                        post.categories.forEach((category, k) => {
                            queryParams.push(
                                `post[${i}][ranks][${k}]=${category}`);
                        });
                    }
                }
            });
        }

        queryParams = queryParams.join('&');

        const {data, headers} = await axios.get(addAuthKey(
            `${apiUrl}?r=api/worker/list-with-photo-extended&${queryParams}`,
            token));

        let formatedData = data.map(worker => {
            let checked = ctx.state.checkboxAllWorkers;

            if (ctx.state.defaultPerformers &&
                ctx.state.defaultPerformers.length) {
                let findedWorker = ctx.state.defaultPerformers.find(
                    workerInModel => workerInModel.id === worker.id);

                if (findedWorker) checked = true;
            }

            return ({
                ...worker,
                checked,
            });
        });

        ctx.commit('setWorkers', {workers: formatedData, page: params.page});
        ctx.commit('setWorkersTotalCount', +headers['x-pagination-total-count']);
        ctx.commit('setWorkersPageCount', +headers['x-pagination-page-count']);

        return formatedData;
    },
    async getResponsibleList(ctx) {
        const token = ctx.rootState.authToken;

        const {data} = await axios.get(
            addAuthKey(`${apiUrl}?r=api/function/responsible`, token));

        return data;
    },
    async getRepeatTypes(ctx) {
        const token = ctx.rootState.authToken;

        const {data} = await axios.get(
            addAuthKey(`${apiUrl}?r=api/function/repeat-types`, token));

        return data;
    },
    async getToolsList(ctx) {
        const token = ctx.rootState.authToken;

        const {data} = await axios.get(
            addAuthKey(`${apiUrl}?r=api/dictionary/index&code=tool`, token));

        return data;
    },
    async createFunction(ctx, params) {

        const token = ctx.rootState.authToken;

        const formData = appendParamsInFormData(params);

        const {data} = await axios.post(
            addAuthKey(`${apiUrl}?r=api/function/create`, token), formData);

        if (data.success) {
            ctx.commit('setFunctionModel', data.model);

            Vue.notify({
                group: 'success-message',
                data: {
                    textMessage: `Новая функция добавлена`,
                },
            });
        } else {
            Vue.notify({
                group: 'error-message',
                data: {
                    textMessage: `Произошла ошибка. Попробуйте ещё раз`,
                },
            });
        }

        return data;
    },
    async updateFunction(ctx, params) {

        const token = ctx.rootState.authToken;

        const formData = appendParamsInFormData(params);

        const {data} = await axios.post(
            addAuthKey(`${apiUrl}?r=api/function/update&id=${params.id}`,
                token), formData);

        if (data.success) {
            ctx.commit('setFunctionModel', data.model);

            Vue.notify({
                group: 'success-message',
                data: {
                    textMessage: `Функция успешно обновлена`,
                },
            });
        } else {
            Vue.notify({
                group: 'error-message',
                data: {
                    textMessage: `Произошла ошибка. Попробуйте ещё раз`,
                },
            });
        }

        return data;
    },
    async deleteFunction(ctx, id) {
        const token = ctx.rootState.authToken;

        const {data} = await axios.delete(
            addAuthKey(`${apiUrl}?r=api/function/delete&id=${id}`, token));

        return data;
    },
    async getStatusesList(ctx) {
        const token = ctx.rootState.authToken;

        const {data} = await axios.get(
            addAuthKey(`${apiUrl}?r=api/function/statuses`, token));

        return data;
    },
    async getIndicatorsList(ctx) {
        const token = ctx.rootState.authToken;

        const {data} = await axios.get(
            addAuthKey(`${apiUrl}?r=api/indicator/list`, token));

        return data;
    },
    async getFunctionModel(ctx, id) {
        const token = ctx.rootState.authToken;

        const {data} = await axios.get(
            addAuthKey(`${apiUrl}?r=api/function/view&id=${id}`, token));

        ctx.commit('setFunctionModel', data);

        return data;
    },
    async saveUserTableColumns({commit, rootState}, columns) {
        const token = rootState.authToken;
        const {userId} = rootState;
        const formData = new FormData();

        formData.append('columns', JSON.stringify(columns));
        formData.append('user_id', userId);

        const url = addAuthKey(`${apiUrl}?r=api/function/save-user-columns`,
            token);

        await axios.post(url, formData);
        commit('setUserTableColumns', columns);
    },
    async getUserTableColumns({commit, rootState}) {
        const token = rootState.authToken;
        const {userId} = rootState;
        const url = addAuthKey(
            `${apiUrl}?r=api/function/user-columns&user_id=${userId}`, token);

        const {data} = await axios.get(url);
        commit('setUserTableColumns', data.message.columns);
    },
    async getTableColumns({commit, rootState}) {
        const token = rootState.authToken;
        const {data} = await axios.get(
            addAuthKey(`${apiUrl}/?r=api/function/columns`, token));

        commit('setTableColumns', data.columns);
    },
    async getFunctions({commit, rootState}, params) {
        const token = rootState.authToken;
        const {sort, page, filters} = params;

        const form_data = new FormData();

        if (filters && filters.columnFilters) {
            for (let filterName in filters.columnFilters) {
                form_data.append(`params[${filterName}_search_type]`,
                    filters.columnFilters[filterName].searchType);

                if (filters.columnFilters[filterName].type === 'date' &&
                    filters.columnFilters[filterName].searchType === 'range') {
                    form_data.append(`params[${filterName}_value]`,
                        filters.columnFilters[filterName].value[0]);
                    form_data.append(`params[${filterName}_value_end]`,
                        filters.columnFilters[filterName].value[1]);
                } else if (filters.columnFilters[filterName].type ===
                    'number' && filters.columnFilters[filterName].searchType ===
                    '4') {
                    form_data.append(`params[${filterName}_value]`,
                        filters.columnFilters[filterName].value.start);
                    form_data.append(`params[${filterName}_value_end]`,
                        filters.columnFilters[filterName].value.end);
                } else {
                    form_data.append(`params[${filterName}_value]`,
                        filters.columnFilters[filterName].value);
                }
            }
        }

        if (filters && filters.globalFilters) {
            for (let filterName in filters.globalFilters) {
                form_data.append(`params[${filterName}_search_type]`,
                    filters.globalFilters[filterName].searchType);

                if (filters.globalFilters[filterName].type === 'date' &&
                    filters.globalFilters[filterName].searchType === 'range') {
                    form_data.append(`params[${filterName}_value]`,
                        filters.globalFilters[filterName].value[0]);
                    form_data.append(`params[${filterName}_value_end]`,
                        filters.globalFilters[filterName].value[1]);
                } else if (filters.globalFilters[filterName].type ===
                    'number' && filters.globalFilters[filterName].searchType ===
                    '4') {
                    form_data.append(`params[${filterName}_value]`,
                        filters.globalFilters[filterName].value.start);
                    form_data.append(`params[${filterName}_value_end]`,
                        filters.globalFilters[filterName].value.end);
                } else {
                    form_data.append(`params[${filterName}_value]`,
                        filters.globalFilters[filterName].value);
                }
            }
        }

        if (params.is_deleted === 0 || params.is_deleted ===
            1) form_data.append('params[is_deleted]', params.is_deleted);

        if (sort) form_data.append('params[sort]', sort);

        const url = addAuthKey(`${apiUrl}/?r=api/function/index${page > -1 ?
            '&page=' + page :
            ''}`, token);
        // const url = `https://hrmdemo3.doxsw.com/?r=api/function/index${page > -1 ? '&page=' + page : ''}&auth_key=${token}`;

        const {data, headers} = await axios.post(url, form_data);

        commit('setTotalItemsCount', +headers['x-pagination-total-count']);
        commit('setTotalPages', +headers['x-pagination-page-count']);

        return data;
    },
};

const getters = {};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
